/**
 * Wedding-day timeline tools (Plan dnia) — read the schedule and create,
 * update or delete events. Reads come from the live snapshot via
 * ctx.dayPlanEvents(); writes go through the platform operations port.
 *
 * Time is a local hour/minute pair (the venue's clock, not a device's
 * timezone). `personId` ties an event to a calendar column declared on the
 * wedding doc (`dayPlanPeople`); an empty string means the first column.
 */

import { Schema } from "firebase/ai";
import {
  DayPlanEventSchema,
  type DayPlanEvent,
  type DayPlanPerson,
} from "@amazing-wedding/shared-types";
import type { DayPlanEventDraft, DayPlanEventPatch } from "../operations";
import type { ToolContext } from "../types";
import type { PlannerTool } from "./registry";
import { asBoolean, asNumber, asString, requireNumber, requireString } from "./helpers";

const DayPlanEventDraftSchema = DayPlanEventSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

function events(ctx: ToolContext): DayPlanEvent[] {
  if (!ctx.dayPlanEvents) {
    throw new Error("The day-plan timeline is not available in this session.");
  }
  return ctx.dayPlanEvents();
}

function columns(ctx: ToolContext): DayPlanPerson[] {
  return [...(ctx.wedding().dayPlanPeople ?? [])].sort((a, b) => a.order - b.order);
}

function columnLabel(ctx: ToolContext, personId: string): string {
  if (!personId) return "—";
  return columns(ctx).find((p) => p.id === personId)?.label ?? "—";
}

/**
 * Validate a model-supplied personId against the wedding's columns. Empty /
 * absent is allowed (the UI falls it back to the first column). An unknown id
 * is rejected so events never point at a column that no longer exists.
 */
function resolvePersonId(value: unknown, ctx: ToolContext): string {
  const id = asString(value);
  if (!id) return "";
  if (!columns(ctx).some((p) => p.id === id)) {
    const valid = columns(ctx)
      .map((p) => `${p.id} (${p.label})`)
      .join(", ");
    throw new Error(
      `Unknown personId "${id}". Call list_day_plan to get column IDs. Valid columns: ${valid || "none"}.`,
    );
  }
  return id;
}

/** Next sort order so a new event lands at the end of the timeline. */
function nextOrder(ctx: ToolContext): number {
  const list = events(ctx);
  return list.length === 0 ? 0 : Math.max(...list.map((e) => e.order)) + 1;
}

function eventLabel(ctx: ToolContext, id: string): string {
  const e = events(ctx).find((ev) => ev.id === id);
  if (!e) return id;
  const clock = `${String(e.hour).padStart(2, "0")}:${String(e.minute).padStart(2, "0")}`;
  return `${clock} ${e.title || "—"}`.trim();
}

const EVENT_INPUT_PROPERTIES = {
  title: Schema.string({ description: 'Event label, e.g. "Pierwszy taniec".' }),
  hour: Schema.number({ description: "Hour in 24h format (0–23)." }),
  minute: Schema.number({ description: "Minute (0–59)." }),
  durationMinutes: Schema.number({ description: "Duration in minutes (0–999). Defaults to 0." }),
  notes: Schema.string({ description: "Free-form notes: logistics, contacts, reminders." }),
  icon: Schema.string({ description: "Icon key (see Plan dnia icons). Defaults to 'dot'." }),
  personId: Schema.string({
    description: "Column this event belongs to (from list_day_plan). Omit or empty = first column.",
  }),
  allColumns: Schema.boolean({
    description:
      "When true the event spans every column (a shared moment such as the ceremony); personId is ignored.",
  }),
};

export const dayPlanTools: PlannerTool[] = [
  {
    declaration: {
      name: "list_day_plan",
      description:
        "List the wedding-day timeline (Plan dnia): its calendar columns and every event with its exact ID, time, duration and notes. Use this to answer schedule questions and to resolve an event's ID before update_day_plan_event or delete_day_plan_event.",
      parameters: Schema.object({ properties: {} }),
    },
    destructive: false,
    describe: () => "Plan dnia",
    execute: async (_args, ctx) => {
      const cols = columns(ctx);
      const list = [...events(ctx)].sort(
        (a, b) => a.order - b.order || a.hour * 60 + a.minute - (b.hour * 60 + b.minute),
      );
      return {
        columns: cols.map((p) => ({ id: p.id, label: p.label })),
        count: list.length,
        events: list.map((e) => ({
          id: e.id,
          time: `${String(e.hour).padStart(2, "0")}:${String(e.minute).padStart(2, "0")}`,
          hour: e.hour,
          minute: e.minute,
          durationMinutes: e.durationMinutes,
          title: e.title,
          notes: e.notes,
          icon: e.icon,
          personId: e.personId,
          column: e.allColumns ? "all" : columnLabel(ctx, e.personId),
          allColumns: e.allColumns ?? false,
        })),
      };
    },
  },
  {
    declaration: {
      name: "create_day_plan_event",
      description:
        "Add an event to the wedding-day timeline (Plan dnia). Use when the couple wants to schedule a moment of the day (ceremony, photos, first dance, etc.). Time is the venue's local clock as hour + minute.",
      parameters: Schema.object({
        properties: { ...EVENT_INPUT_PROPERTIES },
        optionalProperties: ["title", "durationMinutes", "notes", "icon", "personId", "allColumns"],
      }),
    },
    destructive: false,
    describe: (args) => {
      const clock = `${String(asNumber(args.hour) ?? 0).padStart(2, "0")}:${String(
        asNumber(args.minute) ?? 0,
      ).padStart(2, "0")}`;
      return `Dodanie punktu planu dnia: ${clock} ${asString(args.title) ?? ""}`.trim();
    },
    execute: async (args, ctx) => {
      const allColumns = asBoolean(args.allColumns) ?? false;
      const draft: DayPlanEventDraft = DayPlanEventDraftSchema.parse({
        title: asString(args.title) ?? "",
        hour: requireNumber(args, "hour"),
        minute: requireNumber(args, "minute"),
        durationMinutes: asNumber(args.durationMinutes) ?? 0,
        notes: asString(args.notes) ?? "",
        icon: asString(args.icon) ?? "dot",
        personId: allColumns ? "" : resolvePersonId(args.personId, ctx),
        allColumns,
        order: nextOrder(ctx),
      });
      const id = await ctx.operations.dayPlan.createDayPlanEvent(ctx.weddingId, draft);
      return { ok: true, eventId: id, title: draft.title };
    },
  },
  {
    declaration: {
      name: "update_day_plan_event",
      description:
        "Update an existing Plan dnia event. You MUST first call list_day_plan to obtain the exact eventId — never invent an ID. Only the fields you pass are changed.",
      parameters: Schema.object({
        properties: {
          eventId: Schema.string({ description: "Exact event ID from list_day_plan." }),
          ...EVENT_INPUT_PROPERTIES,
        },
        optionalProperties: [
          "title",
          "hour",
          "minute",
          "durationMinutes",
          "notes",
          "icon",
          "personId",
          "allColumns",
        ],
      }),
    },
    destructive: false,
    describe: (args, ctx) =>
      `Aktualizacja punktu planu dnia: ${eventLabel(ctx, asString(args.eventId) ?? "")}`,
    execute: async (args, ctx) => {
      const eventId = requireString(args, "eventId");
      if (!events(ctx).some((e) => e.id === eventId)) {
        throw new Error(`No day-plan event with ID "${eventId}". Call list_day_plan first.`);
      }
      const patch: DayPlanEventPatch = {};
      const title = asString(args.title);
      const hour = asNumber(args.hour);
      const minute = asNumber(args.minute);
      const durationMinutes = asNumber(args.durationMinutes);
      const notes = asString(args.notes);
      const icon = asString(args.icon);
      const allColumns = asBoolean(args.allColumns);
      if (title !== undefined) patch.title = title;
      if (hour !== undefined) patch.hour = hour;
      if (minute !== undefined) patch.minute = minute;
      if (durationMinutes !== undefined) patch.durationMinutes = durationMinutes;
      if (notes !== undefined) patch.notes = notes;
      if (icon !== undefined) patch.icon = icon;
      if (allColumns !== undefined) patch.allColumns = allColumns;
      if (args.personId !== undefined) patch.personId = resolvePersonId(args.personId, ctx);
      if (Object.keys(patch).length === 0) {
        throw new Error("No fields to update were provided.");
      }
      // Validate the merged result so an out-of-range hour/minute is rejected
      // before it reaches Firestore (same guarantee as create).
      DayPlanEventDraftSchema.partial().parse(patch);
      await ctx.operations.dayPlan.updateDayPlanEvent(ctx.weddingId, eventId, patch);
      return { ok: true, eventId, updated: Object.keys(patch) };
    },
  },
  {
    declaration: {
      name: "delete_day_plan_event",
      description:
        "Permanently delete a Plan dnia event. You MUST first call list_day_plan to obtain the exact eventId.",
      parameters: Schema.object({
        properties: {
          eventId: Schema.string({ description: "Exact event ID from list_day_plan." }),
        },
      }),
    },
    destructive: true,
    describe: (args, ctx) =>
      `Usunięcie punktu planu dnia: ${eventLabel(ctx, asString(args.eventId) ?? "")}`,
    execute: async (args, ctx) => {
      const eventId = requireString(args, "eventId");
      if (!events(ctx).some((e) => e.id === eventId)) {
        throw new Error(`No day-plan event with ID "${eventId}". Call list_day_plan first.`);
      }
      await ctx.operations.dayPlan.deleteDayPlanEvent(ctx.weddingId, eventId);
      return { ok: true, eventId };
    },
  },
];
